import { create } from 'zustand';
import { decodeJwt } from '@/lib/jwt';

export interface PlatformOperator {
  id: string;
  email: string;
  fullName: string;
  role: string;
  permissions: string[];
}

/**
 * Estado da sessão no carregamento da página (C8 da change platform-backoffice-hardening).
 *
 * - `restoring`: a renovação pelo cookie `platform_refresh` ainda não respondeu; as rotas
 *   protegidas esperam, sem mandar ao login.
 * - `authenticated`: há access token em memória (renovado pelo cookie ou recebido no login).
 * - `anonymous`: sem sessão; as rotas protegidas mandam ao login.
 * - `unavailable`: a renovação falhou por 429, 5xx ou rede; a sessão pode estar viva, e a tela
 *   oferece nova tentativa manual, sem limpar nada e sem mandar ao login.
 */
export type SessionStatus = 'restoring' | 'authenticated' | 'anonymous' | 'unavailable';

/**
 * Chave em que versões anteriores do backoffice persistiam o par de tokens no `localStorage`.
 * Nada mais grava nela; o carregamento a apaga uma vez ({@link purgeLegacyAuthStorage}).
 */
export const LEGACY_AUTH_STORAGE_KEY = 'platform-admin-auth';

interface AuthState {
  /** Só em memória: o refresh vive no cookie `HttpOnly`, fora do alcance do JavaScript. */
  accessToken: string | null;
  operator: PlatformOperator | null;
  sessionStatus: SessionStatus;
  /** Motivo mostrado com `unavailable`; nulo nos demais estados. */
  sessionError: string | null;

  setAccessToken: (access: string) => void;
  setSessionStatus: (status: Exclude<SessionStatus, 'authenticated'>, error?: string | null) => void;
  clearAuth: () => void;
}

/**
 * Selector — current operator role string, or null if not authenticated.
 * Centralises the Admin-only menu / page gating across the app
 * (design D1 / D10 / D14.2 of change platform-backoffice-account-management).
 */
export const selectCurrentRole = (state: AuthState): string | null =>
  state.operator?.role ?? null;

/**
 * Seletor — o operador tem a permissão `platform.*` informada?
 *
 * O critério é a lista `permissions` que veio do token, com igualdade exata,
 * nunca o nome do papel: quem decide o que cada papel pode é a API
 * (`PlatformTokenService.GetPermissionsForRole`), e a tela só segue o mapa.
 * Sem operador, a resposta é `false`. Esconder o botão não autoriza nada — a
 * rota responde 403 do mesmo jeito (change platform-backoffice-hardening, C1).
 */
export const selectHasPermission =
  (permission: string) =>
  (state: AuthState): boolean =>
    state.operator?.permissions?.includes(permission) ?? false;

/**
 * Marcador da API que já decide concessão, cortesia e nota pela permissão
 * nomeada (emenda C1 da W-0.10, change platform-backoffice-hardening, 2.7).
 * A API nova dá esta permissão a todo papel; um token sem ela veio da imagem
 * anterior da API, que não emite nenhuma das três.
 */
export const WRITE_PERMISSIONS_MARKER = 'platform.order_notes.write';

/** Ações de escrita que a 2.7 passou do papel para a permissão nomeada. */
export type PlatformWriteAction = 'concessions' | 'planOverrides' | 'orderNotes';

const WRITE_ACTION_PERMISSION: Record<PlatformWriteAction, string> = {
  concessions: 'platform.concessions.write',
  planOverrides: 'platform.plan_overrides.write',
  orderNotes: 'platform.order_notes.write',
};

/**
 * Papéis que a API anterior deixava fazer cada ação (concessão e cortesia
 * pelo `RequirePlatformCommercial`; nota para todo operador, por isso `null`).
 */
const WRITE_ACTION_PREVIOUS_ROLES: Record<PlatformWriteAction, ReadonlySet<string> | null> = {
  concessions: new Set(['Commercial', 'Admin']),
  planOverrides: new Set(['Commercial', 'Admin']),
  orderNotes: null,
};

/**
 * Seletor — o operador pode fazer a ação de escrita de concessão, cortesia ou
 * nota?
 *
 * Com o marcador ({@link WRITE_PERMISSIONS_MARKER}) no token, decide pela
 * permissão exata da ação, como {@link selectHasPermission}. Sem ele, o token
 * veio da API anterior (rollback só da imagem da API), que não emite as três
 * permissões: decidir por elas esconderia botões que a API anterior ainda
 * aceita, então a tela cai para o papel, como antes da 2.7. Sem operador, a
 * resposta é `false`.
 *
 * Não renova a sessão para descobrir a lista nova: um token anterior vale no
 * máximo até o vencimento do access token (ou até recarregar a página), quando o interceptor renova
 * (`docs/release-runbook.md`, "Backoffice: permissões e idempotência").
 */
export const selectCanDoWriteAction =
  (action: PlatformWriteAction) =>
  (state: AuthState): boolean => {
    const operator = state.operator;
    if (!operator) return false;
    if (operator.permissions?.includes(WRITE_PERMISSIONS_MARKER)) {
      return selectHasPermission(WRITE_ACTION_PERMISSION[action])(state);
    }
    const roles = WRITE_ACTION_PREVIOUS_ROLES[action];
    return roles === null || roles.has(operator.role);
  };

function operatorFromToken(token: string): PlatformOperator | null {
  const claims = decodeJwt(token);
  if (!claims) return null;
  return {
    id: claims.sub,
    email: claims.email,
    fullName: claims.full_name,
    role: claims.platform_role,
    permissions: claims.platform_permissions ?? [],
  };
}

/**
 * Sessão do backoffice só em memória (C8 da change platform-backoffice-hardening). Nenhum token
 * vai para `localStorage` nem `sessionStorage`: o access token vive nesta aba e some ao
 * recarregar, e o refresh fica no cookie `platform_refresh` (`HttpOnly`, `SameSite=Strict`,
 * `Path=/api/v1/platform/auth`), que o navegador manda sozinho ao `/refresh`. Ao carregar, o
 * `bootstrapSession` (`lib/session-bootstrap.ts`) troca o cookie por um access novo. As abas
 * dividem o cookie, e não a memória: cada uma renova o próprio access, e o logout numa delas
 * derruba as outras no 401 da sessão revalidada (C4).
 */
export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  operator: null,
  sessionStatus: 'restoring',
  sessionError: null,

  setAccessToken: (access) =>
    set({
      accessToken: access,
      operator: operatorFromToken(access),
      sessionStatus: 'authenticated',
      sessionError: null,
    }),

  setSessionStatus: (status, error = null) => set({ sessionStatus: status, sessionError: error }),

  clearAuth: () =>
    set({ accessToken: null, operator: null, sessionStatus: 'anonymous', sessionError: null }),
}));

/**
 * Apaga o par de tokens que versões anteriores deixaram no `localStorage`
 * ({@link LEGACY_AUTH_STORAGE_KEY}). Roda no carregamento do módulo e no `bootstrapSession`;
 * repetir não faz nada. Storage indisponível (modo privado, política do navegador) não impede o
 * carregamento.
 */
export function purgeLegacyAuthStorage(): void {
  try {
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
  } catch {
    // Sem storage, não há o que apagar.
  }
}

if (typeof window !== 'undefined') {
  purgeLegacyAuthStorage();
}

/** Hook de {@link selectHasPermission}, para os componentes. */
export function useHasPlatformPermission(permission: string): boolean {
  return useAuthStore(selectHasPermission(permission));
}

/** Hook de {@link selectCanDoWriteAction}, para os componentes. */
export function useCanDoWriteAction(action: PlatformWriteAction): boolean {
  return useAuthStore(selectCanDoWriteAction(action));
}
